import ImmutableMap from "app/utils/immutable/map"
import * as bluebird from "bluebird"
import * as _ from "lodash"
import * as moment from "moment"

interface INiceAgeOptions {
    yearsUnits?: string
    showMonths?: boolean
}

interface IQueryStringHash {
    [key: string]: string | boolean | number
}

type HashedThings<K extends string, V> = { [key in K]?: V }
type HashedNumbers<K extends string> = HashedThings<K, number> //{[key in K]?: number }
type HasWhen = { [key in "when"]: moment.Moment }

const showReasonableCurrencies = true
const currency = {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
}

const byWhen = (item: any): number => {
    if (!item.when) {
        // tslint:disable-next-line:no-console
        console.error("byWhen - no when!!!")
        // tslint:disable-next-line:no-debugger
        debugger
        return 0
    }
    return item.when.unix()
}

function checkPasswordStrength(pw: string): string {
    pw = pw + "" // force as string
    let numUpperCase = 0
    let numLowerCase = 0
    let numSymbols = 0
    let numNumerics = 0
    const symbols = "!\"#$%&'()*+,-./;:<=>?@[\\]^_`{|}~"

    for (let i = 0; i < pw.length; i++) {
        const ch = pw[i]
        if (isLowerCase(ch)) numLowerCase++
        else if (isUpperCase(ch)) numUpperCase++
        else if (isDigit(ch)) numNumerics++
        else if (symbols.indexOf(ch) >= 0) numSymbols++
    }

    const rules = {
        length: "at least 8 characters",
        uppercase: "at least one uppercase character",
        lowercase: "at least one lowercase character",
        numbers: "at least one number",
        symbols: `and at least one of ${symbols}`,
    }
    const markError = (s: string) => {
        numErrors++
        return "<strong>" + s + "</strong>"
    }
    let numErrors = 0

    if (pw.length < 8) {
        rules.length = markError(rules.length)
    }

    if (0 === numUpperCase) {
        rules.uppercase = markError(rules.uppercase)
    }

    if (0 === numLowerCase) {
        rules.lowercase = markError(rules.lowercase)
    }

    if (0 === numNumerics) {
        rules.numbers = markError(rules.numbers)
    }

    if (0 === numSymbols) {
        rules.symbols = markError(rules.symbols)
    }

    if (0 === numErrors) {
        return undefined
    }

    const r = []
    for (const key in rules) {
        r.push(rules[key])
    }
    return "Passwords must contain " + r.join(", ")
}

/**
 * Compares two moments within some tolerance amount.
 *
 * @param a First moment
 * @param b Second moment
 * @param tolerance The allowed tolerance
 * @param units The units of tolerance
 *
 * @returns < 0 if a < b
 *          = 0 if a = b
 *          > 0 if a > b
 */
function dateComparer(
    a: moment.Moment,
    b: moment.Moment,
    tolerance: number,
    units: moment.unitOfTime.Diff = "days"
): number {
    const d = a.diff(b, units)
    return round(d / tolerance)
}

function makeDebug(enabled) {
    return (...rest) => {
        if (enabled) {
            // tslint:disable-next-line:no-console
            console.log.apply(console, rest)
        }
    }
}

const log = makeDebug(false)

/**
 * Deep diff between two object, using lodash
 * @param  {Object} o1 Object compared
 * @param  {Object} o2   Object to compare with
 * @return {Object}        Return a new object who represent the diff
 */
function difference(o1, o2) {
    const changes = (object, base) => {
        return _.transform(object, (result, value, key) => {
            if (!_.isEqual(value, base[key])) {
                result[key] =
                    _.isObject(value) && _.isObject(base[key])
                        ? changes(value, base[key])
                        : value
            }
        })
    }
    return changes(o2, o2)
}

function durationAsFraction(duration: moment.Duration): number {
    return duration.years() + duration.months() / 12
}

function firstDiff(a: string, b: string) {
    if (a === b) {
        return "same"
    }
    const z = Math.min(a.length, b.length)
    for (let i = 0; i < z; i++) {
        const aa = a.charAt(i)
        const bb = b.charAt(i)
        if (aa !== bb) {
            return (
                a +
                "-- diff at pos: " +
                i +
                ", a-rem: " +
                a.substr(i) +
                ", b-rem: " +
                b.substr(i)
            )
        }
    }
    return "a len: " + a.length + ", b len: " + b.length
}

function fractionalYearAndMonth(when: moment.Moment): number {
    return when.year() + when.month() / 12
}

function fractionalYear(when: moment.Moment): number {
    const soy = when.clone().startOf("year")
    const qq = when.diff(soy, "years", true)
    return qq + soy.year()
    //return when.year() + when.month() / 12
}

function fromFractionalYear(fracYear: number): moment.Moment {
    const y = Math.floor(fracYear)
    const m = (fracYear % 1) * 12
    return moment.utc({ year: y, month: m })
}

// Given a cookie key `name`, returns the value of
// the cookie or `null`, if the key is not found.
function getCookie(name: string): string {
    const nameLenPlus = name.length + 1
    return (
        document.cookie
            .split(";")
            .map((c) => c.trim())
            .filter((cookie) => {
                return cookie.substring(0, nameLenPlus) === `${name}=`
            })
            .map((cookie) => {
                return decodeURIComponent(cookie.substring(nameLenPlus))
            })[0] || null
    )
}

/*
 * Compare two objects by reducing an array of keys in obj1, having the
 * keys in obj2 as the intial value of the result. Key points:
 *
 * - All keys of obj2 are initially in the result.
 *
 * - If the loop finds a key (from obj1, remember) not in obj2, it adds
 *   it to the result.
 *
 * - If the loop finds a key that are both in obj1 and obj2, it compares
 *   the value. If it's the same value, the key is removed from the result.
 */
function getObjectDiff(obj1, obj2) {
    const diff = Object.keys(obj1).reduce((result, key) => {
        if (!obj2.hasOwnProperty(key)) {
            result.push(key)
        } else if (_.isEqual(obj1[key], obj2[key])) {
            const resultKeyIndex = result.indexOf(key)
            result.splice(resultKeyIndex, 1)
        }
        return result
    }, Object.keys(obj2))

    return diff
}

/**
 * Extract a list of object property names that match the specified list.
 *
 * @param source - thr source object
 * @param desiredFields - the list of possiblefields to match
 *
 * @returns string[] - a list of property names that exist in both source and desiredFields
 */
function keysUsed(source: any, desiredFields: string[]) {
    return _(source).keysIn().intersection(desiredFields).value()
}

function getQueryParams(url?: string): IQueryStringHash {
    const r = {}
    url = url || window.location.href
    if (!url) {
        return r
    }
    const from = url.indexOf("?")
    if (from < 0) {
        return r
    }
    const hashes = url.slice(from + 1).split("&")

    for (const hash of hashes) {
        const kvp = hash.split("=")
        let key = kvp[0]
        const value = kvp[1]
        if (key) {
            const h = key.indexOf("#")
            if (h >= 0) {
                key = hash[1].substring(0, h)
            }
        }
        r[key] = value || true
    }

    return r
}

function isDigit(aChar) {
    const myCharCode = aChar.charCodeAt(0)

    return myCharCode > 47 && myCharCode < 58
}

function isLowerCase(str) {
    return str == str.toLowerCase() && str != str.toUpperCase()
}

function isUpperCase(str) {
    return str == str.toUpperCase() && str != str.toLowerCase()
}

function isGoodDuration(d: moment.Duration): boolean {
    if (!d) {
        return false
    }
    return moment.isDuration(d) && 0 !== d.asMonths()
}

/**
 * Check if this code is running in production.
 *
 * @retruns true, iff the code is running in production.
 */
function isProductionEnvironment(): boolean {
    const hostname = window.location.hostname.toLowerCase()
    const isProd =
        hostname !== "localhost" &&
        !hostname.startsWith("192.168") &&
        !hostname.startsWith("172.20") &&
        !hostname.startsWith("tjd-macbook") &&
        !hostname.startsWith("wac-a-mac") &&
        !hostname.startsWith("black-beauty")
    log("isProductionEnvironment is returning: " + isProd)
    return isProd
}

function isInternetExplorer() {
    const ua = window.navigator.userAgent

    const msie = ua.indexOf("MSIE ")
    if (msie > 0) {
        // IE 10 or older => return version number
        return parseInt(ua.substring(msie + 5, ua.indexOf(".", msie)), 10)
    }

    const trident = ua.indexOf("Trident/")
    if (trident > 0) {
        // IE 11 => return version number
        const rv = ua.indexOf("rv:")
        return parseInt(ua.substring(rv + 3, ua.indexOf(".", rv)), 10)
    }

    const edge = ua.indexOf("Edge/")
    if (edge > 0) {
        // Edge (IE 12+) => return version number
        return parseInt(ua.substring(edge + 5, ua.indexOf(".", edge)), 10)
    }

    // other browser
    return false
}

function isNullOrUndefined(thang) {
    return null == thang || _.isUndefined(thang)
}

function isWindows() {
    return navigator.appVersion.indexOf("Win") !== -1
}

function momentAsMonths(when: moment.Moment): number {
    return when.year() * 12 + when.month()
}

/**
 * returns an array with moving average of the input array
 * @param array - the input array
 * @param chunkSize - the number of elements to include in the moving average calculation
 *  value to determine whether it should be used
 */
function movingAverage(array, chunkSize) {
    const result = []

    // pad beginning of result with null values
    for (let j = 0; j < chunkSize - 1; j++) {
        result.push(null)
    }

    // calculate average for each subarray and add to result
    for (let i = 0, len = array.length - chunkSize; i <= len; i++) {
        const val = _.mean(array.slice(i, i + chunkSize))
        if (isNaN(val)) {
            result.push(null)
        } else {
            result.push(val)
        }
    }

    return result
}

function newGuid(): string {
    let d = new Date().getTime()
    return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
        // tslint:disable-next-line:no-bitwise
        const r = (d + Math.random() * 16) % 16 | 0
        d = Math.floor(d / 16)
        // tslint:disable-next-line:no-bitwise
        return (c === "x" ? r : (r & 0x3) | 0x8).toString(16)
    })
}

function parseFormattedNumber(source: string): number {
    if (!source) {
        return 0
    }
    const z = source.split(".")
    if (z.length >= 2) {
        return NaN
    }
    const whole = z[0]
    const frac = z[1]
    let v = 0
    const clumpSize = 3 // # digits
    let scaler = 1
    let index = whole.length
    let sawSep = false
    let everSawSep = false
    let clumpLength = 0

    while (index > 0) {
        sawSep = false
        clumpLength = 0

        while (index > 0) {
            index--
            const d = whole.substr(index, 1)
            if (isDigit(d)) {
                v = v + (d.charCodeAt(0) - "0".charCodeAt(0)) * scaler
                scaler *= 10
                clumpLength++
            } else {
                if (d !== ",") {
                    return NaN
                }
                if (clumpLength !== clumpSize) {
                    return NaN
                }
                everSawSep = sawSep = true
                break
            }
        }
    }

    // check last clump
    if (sawSep) {
        if (clumpLength === 0) {
            return NaN
        }
    } else {
        if (everSawSep && clumpLength > clumpSize) {
            return NaN
        }
    }

    return v
}

function parseYesNo(s: string): boolean {
    if (!s) {
        return false
    }
    const sl = s.toLocaleLowerCase()
    return sl === "y"
}

/**
 * Return the possessive version of a name. Eg. Joe's from Joe
 * @param name
 */
function personPossessive(name: string, defaultName = "(no name)") {
    if (!name || 0 === name.length) {
        name = defaultName
    }
    return name + "'s"
}

function reasonableWhole(v: number): number {
    if (!v) {
        return 0
    }
    v = round(v)
    const nDigits = Math.ceil(Math.log10(v))
    const useDigits = Math.floor(nDigits / 2)
    const m = 10 ** useDigits
    return round(v / m) * m
}

/**
 * Round a value to nearest integer. Unlike the standard
 * math.round, this version handles negative numbers correctly.
 *
 * @param v the value to round.
 *
 * @returns the nearest integer value.
 */
function round(v: number): number {
    return Math.sign(v) * Math.round(Math.abs(v)) + 0
}

/**
 * Returns the server endpoint.
 *
 * @param map - the map of option-to-endpoint
 */
const selectWebservicesEndpoint = (map: HashedThings<string, string>) => {
    let server = getQueryParams().server as string
    if (!server) {
        return map.themajordomo
    }
    server = server.split("#")[0]
    const ep = map[server]
    if (!ep) {
        throw new Error("No support for server=" + server)
    }
    log("alternate server selected: " + ep)
    return ep
}

/**
 * moments can be too precise. Sometimes you want to just deal with dates that are in the same
 * month, or year, or quarter
 *
 * @param when The moment to smooth
 * @param how  The smoothing method
 */
function smoothedDate(
    when: moment.Moment,
    how: moment.unitOfTime.StartOf = "month"
): moment.Moment {
    return when.clone().startOf(how)
}

function toCurrency(v: number, fullPrecision: boolean = true): string {
    if (_.isNil(v)) {
        return "$0"
    }

    const isNeg = v < 0
    if (isNeg) {
        v = -v
    }

    v = showReasonableCurrencies && !fullPrecision ? reasonableWhole(v) : v
    //const tx = v.toLocaleString('en', currency);
    //has .nn const tx = '$' + v.toFixed(2).replace(/(\d)(?=(\d{3})+\.)/g, '$1,')
    const tx = "$" + v.toFixed().replace(/(\d)(?=(\d{3})+(,|$))/g, "$1,")
    return isNeg ? "(" + tx + ")" : tx
}

// needs to be a function so that a cloned copy can be returned (calles tend to want to mutate it)
const todaysDate = () => moment.utc().startOf("day")

function toNumber(num: string): number {
    if (_.isUndefined(num)) {
        return null
    }

    // remove formatting characters
    if (_.isString(num)) {
        num = num.replace(/[$,]+/g, "")
    }

    const v = Number(num).valueOf()
    //log('toNumber: ' + num + ' translates to ' + (typeof v) + ' is ' + v)

    return v
}

function toPercentage(num: number, fixedAmount = 1): string {
    if (_.isNil(num) || isNaN(num)) {
        return "0%"
    }

    const percentage = (num * 100).toFixed(fixedAmount)
    return `${percentage}%`
}

function toNiceAge(when: moment.Duration, options?: INiceAgeOptions): string {
    if (!moment.isDuration(when)) {
        return null
    }

    options = _.defaults({}, options, { yearsUnits: "", showMonths: true })

    const y = when.years()
    let m = round(when.months())
    if (!options.showMonths) {
        m = 0
    }

    if (0 === m) {
        const sep = options.yearsUnits === "" ? "" : " "
        return y + sep + options.yearsUnits
    }
    if (m === Math.floor(m)) {
        return y + " yr, " + m + " mo"
    }
    return 0 === m ? y + "" : y + " yr, " + m + " mo"
}

function toYesNo(what: boolean): string {
    return what ? "Y" : "N"
}

/**
 * A replacement for native 'fetch' that ensures that the promise returned
 * has a 'finally' method attached to it.
 *
 * @param request - the fetch request
 */
function xfetch(request: RequestInfo): Promise<Response> {
    return new bluebird.Promise((resolve, reject) => {
        return fetch(request)
            .then((what) => resolve(what))
            .catch((reason) => reject(reason))
    })
}

export {
    INiceAgeOptions,
    byWhen,
    checkPasswordStrength,
    currency,
    dateComparer,
    makeDebug,
    difference,
    durationAsFraction,
    firstDiff,
    fractionalYear,
    fractionalYearAndMonth,
    fromFractionalYear,
    getCookie,
    getObjectDiff,
    HashedNumbers,
    HashedThings,
    ImmutableMap,
    IQueryStringHash,
    getQueryParams,
    isDigit,
    isLowerCase,
    isUpperCase,
    isGoodDuration,
    isProductionEnvironment,
    isInternetExplorer,
    isNullOrUndefined,
    isWindows,
    keysUsed,
    momentAsMonths,
    movingAverage,
    newGuid,
    parseFormattedNumber,
    parseYesNo,
    personPossessive,
    reasonableWhole,
    round,
    selectWebservicesEndpoint,
    smoothedDate,
    toCurrency,
    todaysDate,
    toNumber,
    toPercentage,
    toNiceAge,
    toYesNo,
    xfetch,
}



// WEBPACK FOOTER //
// ./src/app/utils/index.ts